import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  CandidateProcedure,
  CandidateProcedureDecision,
  NewCandidateVerificationOpts,
  RequestCandidateVerificationSupplementaryInfoOpts,
  SaveCandidateVerificationResultOpts,
  SearchCandidateVerificationsOpts,
  ServiceTypeOptional,
  UpdateCandidateVerificationDecisionOpts,
  UpdateCandidateVerificationDetailsOpts,
  UpdateCandidateVerificationStatusOpts,
} from '@interfaces';
import { AppState } from '@state';
import * as cvmActions from './cvm.actions';
import * as cvmSelectors from './cvm.selectors';

@Injectable()
export class CVMFacade {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly actions = inject(Actions);

  public candidateVerifications$ = this.store.select(cvmSelectors.selectCandidateVerifications);
  public candidateVerificationsPagination$ = this.store.select(cvmSelectors.selectCandidateVerificationsPagination);
  public loadingCandidateVerifications$ = this.store.select(cvmSelectors.selectLoadingCandidateVerifications);

  public candidateVerificationDetails$ = this.store.select(cvmSelectors.selectCandidateVerificationDetails);
  public loadingCandidateVerificationDetails$ = this.store.select(
    cvmSelectors.selectLoadingCandidateVerificationDetails
  );
  public updatingCandidateVerificationStatus$ = this.store.select(
    cvmSelectors.selectUpdatingCandidateVerificationStatus
  );
  public savingCandidateVerificationResult$ = this.store.select(
    cvmSelectors.selectLoadingSavingCandidateVerificationResult
  );
  public requestingCandidateVerificationSupplementaryInfo$ = this.store.select(
    cvmSelectors.selectRequestingCandidateVerificationSupplementaryInfo
  );
  public downloadingCandidateVerificationFileId$ = this.store.select(
    cvmSelectors.selectDownloadingCandidateVerificationFileId
  );
  public deletingCandidateVerification$ = this.store.select(cvmSelectors.selectDeletingCandidateVerification);

  public candidateVerificationHistory$ = this.store.select(cvmSelectors.selectCandidateVerificationHistory);
  public loadingCandidateVerificationHistory$ = this.store.select(
    cvmSelectors.selectLoadingCandidateVerificationHistory
  );

  public searchCandidateVerificationsSuccess$ = this.actions.pipe(
    ofType(cvmActions.searchCandidateVerificationsSuccess)
  );
  public searchCandidateVerificationsError$ = this.actions.pipe(ofType(cvmActions.searchCandidateVerificationsError));

  public getCandidateVerificationDetailsSuccess$ = this.actions.pipe(
    ofType(cvmActions.getCandidateVerificationDetailsSuccess)
  );
  public getCandidateVerificationDetailsError$ = this.actions.pipe(
    ofType(cvmActions.getCandidateVerificationDetailsError)
  );

  public getCandidateVerificationHistorySuccess$ = this.actions.pipe(
    ofType(cvmActions.getCandidateVerificationHistorySuccess)
  );
  public getCandidateVerificationHistoryError$ = this.actions.pipe(
    ofType(cvmActions.getCandidateVerificationHistoryError)
  );

  public addNewCandidateVerificationSuccess$ = this.actions.pipe(ofType(cvmActions.addNewCandidateVerificationSuccess));
  public addNewCandidateVerificationError$ = this.actions.pipe(ofType(cvmActions.addNewCandidateVerificationError));

  public updateCandidateVerificationBasicDetailsSuccess$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationBasicDetailsSuccess)
  );
  public updateCandidateVerificationBasicDetailsError$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationBasicDetailsError)
  );

  public updateCandidateVerificationStatusSuccess$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationStatusSuccess)
  );
  public updateCandidateVerificationStatusError$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationStatusError)
  );

  public saveCandidateVerificationResultSuccess$ = this.actions.pipe(
    ofType(cvmActions.saveCandidateVerificationResultSuccess)
  );
  public saveCandidateVerificationResultError$ = this.actions.pipe(
    ofType(cvmActions.saveCandidateVerificationResultError)
  );

  public setCandidateVerificationDecisionSuccess$ = this.actions.pipe(
    ofType(cvmActions.setCandidateVerificationDecisionSuccess)
  );
  public setCandidateVerificationDecisionError$ = this.actions.pipe(
    ofType(cvmActions.setCandidateVerificationDecisionError)
  );

  public updateCandidateVerificationProcedureDecisionSuccess$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationProcedureDecisionSuccess)
  );
  public updateCandidateVerificationProcedureDecisionError$ = this.actions.pipe(
    ofType(cvmActions.updateCandidateVerificationProcedureDecisionError)
  );

  public addCandidateVerificationExtraServiceSuccess$ = this.actions.pipe(
    ofType(cvmActions.addCandidateVerificationExtraServiceSuccess)
  );
  public addCandidateVerificationExtraServiceError$ = this.actions.pipe(
    ofType(cvmActions.addCandidateVerificationExtraServiceError)
  );

  public requestCandidateVerificationSupplementaryInfoSuccess$ = this.actions.pipe(
    ofType(cvmActions.requestCandidateVerificationSupplementaryInfoSuccess)
  );
  public requestCandidateVerificationSupplementaryInfoError$ = this.actions.pipe(
    ofType(cvmActions.requestCandidateVerificationSupplementaryInfoError)
  );

  public regenerateCandidateFormLinkAndPinSuccess$ = this.actions.pipe(
    ofType(cvmActions.regenerateCandidateFormLinkAndPinSuccess)
  );
  public regenerateCandidateFormLinkAndPinError$ = this.actions.pipe(
    ofType(cvmActions.regenerateCandidateFormLinkAndPinError)
  );

  public extendCandidateVerificationFormValiditySuccess$ = this.actions.pipe(
    ofType(cvmActions.extendCandidateVerificationFormValiditySuccess)
  );
  public extendCandidateVerificationFormValidityError$ = this.actions.pipe(
    ofType(cvmActions.extendCandidateVerificationFormValidityError)
  );

  public deleteCandidateVerificationSuccess$ = this.actions.pipe(ofType(cvmActions.deleteCandidateVerificationSuccess));
  public deleteCandidateVerificationError$ = this.actions.pipe(ofType(cvmActions.deleteCandidateVerificationError));

  public openCandidateVerificationFileSuccess$ = this.actions.pipe(
    ofType(cvmActions.openCandidateVerificationFileSuccess)
  );
  public openCandidateVerificationFileError$ = this.actions.pipe(ofType(cvmActions.openCandidateVerificationFileError));

  public searchCandidateVerifications(opts: SearchCandidateVerificationsOpts): void {
    this.store.dispatch(cvmActions.searchCandidateVerifications({ opts }));
  }

  public getCandidateVerificationDetails(candidateVerificationId: string): void {
    this.store.dispatch(cvmActions.getCandidateVerificationDetails({ candidateVerificationId }));
  }

  public getCandidateVerificationHistory(candidateVerificationId: string): void {
    this.store.dispatch(cvmActions.getCandidateVerificationHistory({ candidateVerificationId }));
  }

  public addNewCandidateVerification(opts: NewCandidateVerificationOpts): void {
    this.store.dispatch(cvmActions.addNewCandidateVerification({ opts }));
  }

  public updateCandidateVerificationBasicDetails(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDetailsOpts
  ): void {
    this.store.dispatch(cvmActions.updateCandidateVerificationBasicDetails({ candidateVerificationId, opts }));
  }

  public updateCandidateVerificationStatus(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationStatusOpts
  ): void {
    this.store.dispatch(cvmActions.updateCandidateVerificationStatus({ candidateVerificationId, opts }));
  }

  public saveCandidateVerificationResult(
    candidateVerificationId: string,
    opts: SaveCandidateVerificationResultOpts
  ): void {
    this.store.dispatch(cvmActions.saveCandidateVerificationResult({ candidateVerificationId, opts }));
  }

  public setCandidateVerificationDecision(
    candidateVerificationId: string,
    opts: UpdateCandidateVerificationDecisionOpts
  ): void {
    this.store.dispatch(cvmActions.setCandidateVerificationDecision({ candidateVerificationId, opts }));
  }

  public updateCandidateVerificationProcedureDecision(
    candidateVerificationId: string,
    procedureId: string,
    decision: CandidateProcedureDecision
  ): void {
    this.store.dispatch(
      cvmActions.updateCandidateVerificationProcedureDecision({ candidateVerificationId, procedureId, decision })
    );
  }

  public addCandidateVerificationExtraService(candidateVerificationId: string, serviceType: ServiceTypeOptional): void {
    this.store.dispatch(cvmActions.addCandidateVerificationExtraService({ candidateVerificationId, serviceType }));
  }

  public requestCandidateVerificationSupplementaryInfo(
    candidateVerificationId: string,
    opts: RequestCandidateVerificationSupplementaryInfoOpts
  ): void {
    this.store.dispatch(cvmActions.requestCandidateVerificationSupplementaryInfo({ candidateVerificationId, opts }));
  }

  public regenerateCandidateFormLinkAndPin(candidateVerificationId: string): void {
    this.store.dispatch(cvmActions.regenerateCandidateFormLinkAndPin({ candidateVerificationId }));
  }

  public extendCandidateVerificationFormValidity(candidateVerificationId: string, extendByDays: number): void {
    this.store.dispatch(cvmActions.extendCandidateVerificationFormValidity({ candidateVerificationId, extendByDays }));
  }

  public deleteCandidateVerification(candidateVerificationId: string): void {
    this.store.dispatch(cvmActions.deleteCandidateVerification({ candidateVerificationId }));
  }

  public openCandidateVerificationFile(
    candidateVerificationId: string,
    fileId: string,
    previewTargetKey?: string
  ): void {
    this.store.dispatch(
      cvmActions.openCandidateVerificationFile({ candidateVerificationId, fileId, previewTargetKey })
    );
  }
}
